import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const SelectAction: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Choisir une action",
  disabled = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value || '');
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(option => option.value === selectedValue);

  // Positionner le menu (en portal) juste sous le bouton
  const updatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  };

  useLayoutEffect(() => {
    if (isOpen) updatePosition();
  }, [isOpen]);

  // Garder le menu aligné lors d'un scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    const handleReposition = () => updatePosition();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isOpen]);

  // Fermer le dropdown quand on clique à l'extérieur (bouton ou menu en portal)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        menuRef.current && !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option: SelectOption) => {
    setSelectedValue(option.value);
    setIsOpen(false);
    onChange?.(option.value);
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleDropdown}
        disabled={disabled}
        className={`
          relative w-full min-w-32 px-3 py-1.5 text-left bg-white border border-gray-200
          rounded-md shadow-sm cursor-pointer transition-all duration-200
          hover:border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300
          ${disabled ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : ''}
          ${isOpen ? 'ring-1 ring-gray-300 border-gray-300' : ''}
        `}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={`text-sm ${selectedOption ? 'text-gray-900' : 'text-gray-500'}`}>
              {selectedOption?.label || placeholder}
            </span>
          </div>
          <ChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''
              }`}
          />
        </div>
      </button>

      {/* Dropdown menu (rendu en portal pour ne jamais être rogné) */}
      {isOpen && menuPosition && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: menuPosition.top,
            left: menuPosition.left,
            width: menuPosition.width
          }}
          className="z-[9999] bg-white border border-gray-200 rounded-md shadow-lg"
        >
          <div className="py-1 max-h-60 overflow-auto">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option)}
                className={`
                  w-full px-3 py-1.5 text-left text-sm transition-colors duration-150 group
                  hover:bg-gray-50 focus:outline-none focus:bg-gray-50
                  ${selectedValue === option.value ? 'bg-gray-50' : ''}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {/* Petit rond avec effet hover */}
                    <div className="relative">
                      <div className="w-3 h-3 rounded-full border border-gray-300 bg-white transition-all duration-200">
                        <div className={`absolute inset-0.5 rounded-full transition-all duration-200 ${
                          selectedValue === option.value
                            ? 'bg-gray-500'
                            : 'bg-transparent group-hover:bg-gray-400'
                        }`}></div>
                      </div>
                    </div>
                    <span className="text-gray-700">{option.label}</span>
                  </div>
                  {selectedValue === option.value && (
                    <Check className="w-4 h-4 text-gray-500" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SelectAction;
